<template>
  <div class="saml-settings" data-cy="saml-settings">
    <div class="section-divider"></div>
    <div class="title">SAML SSO</div>

    <div v-if="state.loading" class="mt-4" data-cy="saml-loading">
      <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
    </div>

    <div v-else-if="state.unavailable" class="mt-4 unavailable" data-cy="saml-unavailable">
      SAML not available for this district yet
    </div>

    <template v-else>
      <p class="help-text mt-3" data-cy="saml-provider-help">
        Paste these URLs into {{ providerName }} as the PTC Wizard SAML app.
      </p>

      <div class="title mt-4" style="font-size: 1rem">Give to {{ providerName }} / IdP</div>

      <div class="mt-3">
        <div class="field-label">ACS URL</div>
        <div class="sso-url-row">
          <span data-cy="saml-acs-url">{{ state.acs_url }}</span>
          <v-btn
            icon
            color="transparent"
            size="small"
            data-cy="saml-acs-copy-btn"
            :disabled="!state.acs_url"
            @click="copyToClipboard(state.acs_url)"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </div>
      </div>

      <div class="mt-3">
        <div class="field-label">PTC Wizard Metadata URL</div>
        <div class="sso-url-row">
          <span data-cy="saml-metadata-url">{{ state.metadata_url }}</span>
          <v-btn
            icon
            color="transparent"
            size="small"
            data-cy="saml-metadata-copy-btn"
            :disabled="!state.metadata_url"
            @click="copyToClipboard(state.metadata_url)"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </div>
      </div>

      <p class="help-text mt-5" data-cy="saml-idp-help">
        Then paste {{ providerName }}’s IdP metadata URL here. Entity ID, signing certificate, and
        related values are under Advanced options (filled automatically when import is available, or
        enter them manually).
      </p>

      <div class="title mt-3" style="font-size: 1rem">From {{ providerName }} / IdP</div>

      <v-select
        class="mt-3 custom-input"
        label="Subscriber"
        color="primary"
        :items="state.available_subscribers"
        item-title="title"
        item-value="value"
        v-model="state.subscriber_key"
        hide-details="auto"
        :disabled="state.saving"
        data-cy="saml-subscriber"
      ></v-select>

      <v-text-field
        class="mt-3 custom-input"
        label="IdP Metadata URL"
        color="primary"
        v-model="state.idp_metadata_url"
        hide-details="auto"
        :disabled="state.saving"
        data-cy="saml-idp-metadata-url"
        placeholder="https://…"
      ></v-text-field>

      <v-switch
        class="mt-3"
        color="secondary"
        :model-value="state.enabled"
        label="Enable SAML"
        hide-details
        :disabled="state.saving || (!state.enabled && !canEnable)"
        data-cy="saml-enabled-switch"
        @update:model-value="onEnabledChange"
      ></v-switch>

      <v-btn
        class="mt-4 px-0"
        variant="text"
        color="primary"
        data-cy="saml-advanced-toggle"
        :disabled="state.saving"
        @click="showAdvanced = !showAdvanced"
      >
        {{ showAdvanced ? "Hide" : "Show" }} Advanced options
        <v-icon end>{{ showAdvanced ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </v-btn>

      <div v-show="showAdvanced" class="advanced-options" data-cy="saml-advanced-options">
        <p class="help-text mt-2">
          These values are normally populated from the IdP metadata URL. Adjust only if needed.
        </p>

        <v-text-field
          class="mt-3 custom-input"
          label="IdP Entity ID"
          color="primary"
          v-model="state.idp_entity_id"
          hide-details="auto"
          :disabled="state.saving"
          data-cy="saml-idp-entity-id"
        ></v-text-field>

        <v-textarea
          class="mt-3 custom-input"
          label="IdP X.509 Certificate"
          color="primary"
          v-model="state.idp_x509_cert"
          rows="5"
          auto-grow
          hide-details="auto"
          :disabled="state.saving"
          data-cy="saml-idp-x509-cert"
        ></v-textarea>
        <p class="cert-hint" data-cy="saml-cert-hint">
          Paste the full PEM certificate, including -----BEGIN CERTIFICATE----- /
          -----END CERTIFICATE-----.
        </p>

        <v-text-field
          class="mt-3 custom-input"
          label="Cert expiry date (optional)"
          type="date"
          color="primary"
          v-model="state.cert_expires_at"
          hide-details="auto"
          :disabled="state.saving"
          data-cy="saml-cert-expires-at"
        ></v-text-field>

        <v-text-field
          class="mt-3 custom-input"
          label="IdP SSO URL (optional, not used yet)"
          color="primary"
          v-model="state.idp_sso_url"
          hide-details="auto"
          :disabled="state.saving"
          data-cy="saml-idp-sso-url"
        ></v-text-field>
      </div>

      <div class="d-flex justify-end mt-4">
        <v-btn
          :loading="state.saving"
          :disabled="state.saving || (state.enabled && !canEnable)"
          data-cy="saml-save-btn"
          @click="save"
        >
          Save
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import FetchService from "@/services/fetch.service";
import { useAlertsStore } from "@/stores/alerts";
import { copyToClipboard } from "@/utils";
import { computed, onMounted, reactive, ref, watch } from "vue";
import {
  canEnableSaml,
  samlApiErrorMessage,
  samlFormDefaults,
  trimSamlField,
  type SamlFormState,
  type SamlSubscriberOption,
} from "./utils";

const props = withDefaults(
  defineProps<{
    /** Display name in help text (e.g. Synergy, Finalsite). */
    providerName?: string;
    /** Fallback subscriber_key when API returns null / empty. */
    defaultSubscriberKey?: string;
  }>(),
  {
    providerName: "Synergy",
    defaultSubscriberKey: "synergy",
  }
);

const alertsStore = useAlertsStore();

const showAdvanced = ref(false);

const state = reactive({
  loading: true,
  saving: false,
  unavailable: false,
  acs_url: "",
  metadata_url: "",
  available_subscribers: [] as SamlSubscriberOption[],
  subscriber_key: props.defaultSubscriberKey,
  idp_metadata_url: "",
  idp_entity_id: "",
  idp_sso_url: "",
  idp_x509_cert: "",
  cert_expires_at: "",
  enabled: false,
});

const canEnable = computed(() => canEnableSaml(state.idp_entity_id, state.idp_x509_cert));

const applyForm = (form: SamlFormState) => {
  state.acs_url = form.acs_url;
  state.metadata_url = form.metadata_url;
  state.available_subscribers = form.available_subscribers;
  state.subscriber_key = form.subscriber_key;
  state.idp_metadata_url = form.idp_metadata_url;
  state.idp_entity_id = form.idp_entity_id;
  state.idp_sso_url = form.idp_sso_url;
  state.idp_x509_cert = form.idp_x509_cert;
  state.cert_expires_at = form.cert_expires_at;
  state.enabled = form.enabled && canEnableSaml(form.idp_entity_id, form.idp_x509_cert);
};

const isServerError = (payload: unknown, httpStatus?: number) => {
  if (httpStatus === 500) return true;
  if (payload && typeof payload === "object" && (payload as { status?: number }).status === 500) {
    return true;
  }
  return false;
};

const isSamlSuccess = (data: unknown): boolean =>
  Boolean(data && typeof data === "object" && "acs_url" in (data as object));

const load = async () => {
  state.loading = true;
  state.unavailable = false;
  try {
    const result = await FetchService.fetchData("/integration/saml", "get", undefined, true);
    const httpStatus =
      result && typeof result === "object" && "res" in result
        ? (result as { res?: Response }).res?.status
        : undefined;
    const data =
      result && typeof result === "object" && "data" in result
        ? (result as { data: unknown }).data
        : result;

    if (isServerError(result, httpStatus) || isServerError(data, httpStatus)) {
      state.unavailable = true;
      return;
    }

    if (!isSamlSuccess(data)) {
      state.unavailable = true;
      return;
    }

    applyForm(samlFormDefaults(data, props.defaultSubscriberKey));
  } catch {
    state.unavailable = true;
  } finally {
    state.loading = false;
  }
};

const onEnabledChange = (val: boolean | null) => {
  if (val && !canEnable.value) {
    state.enabled = false;
    return;
  }
  state.enabled = Boolean(val);
};

watch(
  () => [state.idp_entity_id, state.idp_x509_cert] as const,
  () => {
    if (state.enabled && !canEnableSaml(state.idp_entity_id, state.idp_x509_cert)) {
      state.enabled = false;
    }
  }
);

const save = async () => {
  const entityId = trimSamlField(state.idp_entity_id);
  const cert = trimSamlField(state.idp_x509_cert);
  const subscriberKey = trimSamlField(state.subscriber_key) || props.defaultSubscriberKey;
  const ssoUrl = trimSamlField(state.idp_sso_url);
  const idpMetadataUrl = trimSamlField(state.idp_metadata_url);
  let enabled = state.enabled;

  if (enabled && !canEnableSaml(entityId, cert)) {
    enabled = false;
    state.enabled = false;
  }

  state.idp_entity_id = entityId;
  state.idp_x509_cert = cert;
  state.subscriber_key = subscriberKey;
  state.idp_sso_url = ssoUrl;
  state.idp_metadata_url = idpMetadataUrl;

  if (enabled && !canEnableSaml(entityId, cert)) {
    return;
  }

  state.saving = true;
  try {
    const result = await FetchService.fetchData(
      "/integration/saml",
      "put",
      {
        subscriber_key: subscriberKey,
        idp_metadata_url: idpMetadataUrl,
        idp_entity_id: entityId,
        idp_sso_url: ssoUrl,
        idp_x509_cert: cert,
        cert_expires_at: state.cert_expires_at || "",
        enabled,
      },
      true
    );

    const httpStatus =
      result && typeof result === "object" && "res" in result
        ? (result as { res?: Response }).res?.status
        : undefined;
    const data =
      result && typeof result === "object" && "data" in result
        ? (result as { data: unknown }).data
        : result;

    if (isServerError(result, httpStatus) || isServerError(data, httpStatus)) {
      state.unavailable = true;
      alertsStore.addAlert("error", "SAML not available for this district yet");
      return;
    }

    if (httpStatus === 422 || (data && typeof data === "object" && "error" in (data as object))) {
      const message = samlApiErrorMessage(data) || "Failed to save SAML settings";
      alertsStore.addAlert("error", message);
      return;
    }

    if (!isSamlSuccess(data)) {
      const message = samlApiErrorMessage(data) || "Failed to save SAML settings";
      alertsStore.addAlert("error", message);
      return;
    }

    applyForm(samlFormDefaults(data, props.defaultSubscriberKey));
    alertsStore.addAlert("success", "SAML settings saved");
  } catch (error) {
    console.error("Error saving SAML settings:", error);
    alertsStore.addAlert("error", "An unexpected error occurred");
  } finally {
    state.saving = false;
  }
};

onMounted(() => {
  load();
});
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.section-divider {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin: 28px 0 20px;
}

.title {
  color: $primary;
  font-size: 1.25rem;
  font-weight: 600;
}

.help-text {
  color: gray;
  margin: 0;
}

.field-label {
  color: $primary;
  font-weight: 600;
  font-size: 0.95rem;
}

.sso-url-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 8px;
  color: gray;
  word-break: break-all;
}

.cert-hint {
  color: gray;
  font-size: 0.85rem;
  margin: 6px 0 0;
}

.advanced-options {
  margin-top: 4px;
  padding: 12px 0 4px;
  border-top: 1px dashed rgba(0, 0, 0, 0.12);
}

.unavailable {
  color: gray;
}
</style>
